import SubServicePageLayout from "@/components/SubServicePageLayout";
import { usePageContent } from "@/hooks/usePageContent";
import { resolveIcon } from "@/lib/iconResolver";

const fallback = {
  seoTitle: "Conversion Rate Optimization That Turns Traffic into Revenue",
  seoDescription: "Data-driven CRO that compounds. We identify what's blocking conversion and systematically remove friction to maximize revenue from existing traffic.",
  canonicalUrl: "https://xperfomax.com/cro",
  category: "services",
  categoryLabel: "Services / CRO",
  headline: "Conversion Rate Optimization That Turns Traffic into Revenue",
  subheadline: "Data-driven optimization that compounds. We identify what's blocking conversion and systematically remove friction to maximize revenue from existing traffic.",
  ctaText: "Get a CRO audit",
  problemTitle: "Why Most CRO Fails",
  problems: [
    "Traffic increases but revenue stays flat.",
    "Design changes are made without behavioral data to inform them.",
    "Testing happens randomly without prioritized hypotheses.",
    "Conversion insights exist in silos, not compounding systems.",
  ],
  systemTitle: "Our CRO System",
  systemDescription: "User behavior analysis, funnel diagnostics, hypothesis-led experimentation, and continuous optimization loops.",
  services: [
    { name: "Funnel & UX Audits", icon: "Layers", components: ["Full-funnel journey mapping", "Drop-off point identification", "Revenue leak analysis"], outcome: "Clear visibility into where you're losing conversions." },
    { name: "Heatmap & Session Analysis", icon: "Target", components: ["Click and scroll heatmaps", "Session recordings review", "User behavior patterns"], outcome: "Understand what users actually do, not what you assume." },
    { name: "Landing Page Optimization", icon: "MousePointerClick", components: ["Message match optimization", "Above-fold improvements", "Trust signal integration"], outcome: "Landing pages that convert traffic into customers." },
    { name: "A/B Testing Strategy", icon: "FlaskConical", components: ["Hypothesis development", "Statistical significance testing", "Multi-variate testing"], outcome: "Data-driven decisions that compound over time." },
    { name: "Checkout & Form Optimization", icon: "FormInput", components: ["Checkout flow analysis", "Form field optimization", "Cart abandonment reduction"], outcome: "Reduce friction at the moment of conversion." },
    { name: "CRO Reporting & Insights", icon: "BarChart3", components: ["Test result analysis", "Revenue impact reporting", "Continuous learning documentation"], outcome: "A compounding library of conversion insights." },
  ],
  systemShift: {
    title: "The conversion shift.",
    beforeTitle: "Before: Random Optimization",
    afterTitle: "After: Engineered Conversion",
    items: [
      { label: "Conversion Logic", before: "Pages designed for aesthetics, conversion as afterthought", after: "Every element engineered for visitor-to-customer transformation" },
      { label: "Decision Making", before: "Changes based on opinions, HiPPO decisions, gut feel", after: "Data-backed hypotheses with statistical validation" },
      { label: "Testing Approach", before: "Random tests without prioritization or learning capture", after: "Systematic experimentation with compounding insights" },
      { label: "Revenue Attribution", before: "Vanity metrics: bounce rate, time on page", after: "Direct revenue impact per test and optimization" },
      { label: "Funnel Visibility", before: "Drop-offs unknown, friction points invisible", after: "Complete funnel clarity with revenue leak identification" },
    ],
  },
  engagementShape: {
    title: "How CRO engagement unfolds.",
    description: "No fixed timelines. No deliverables lists. Structure that adapts to your traffic patterns and testing velocity.",
  },
  processTitle: "From insight to implementation.",
  processSteps: [
    { number: "01", title: "Funnel Analysis", description: "Comprehensive analysis of your current funnel, identifying friction points and revenue leaks." },
    { number: "02", title: "Hypothesis Creation", description: "Data-backed hypotheses prioritized by potential impact and ease of implementation." },
    { number: "03", title: "Testing & Validation", description: "Rigorous A/B and multivariate tests with proper statistical methodology." },
    { number: "04", title: "Learnings & Scale", description: "Roll out winning variants and apply learnings across your digital properties." },
  ],
  technologyCategories: [
    { title: "Analytics", icon: "LineChart", items: ["GA4", "Mixpanel", "Amplitude"] },
    { title: "Heatmaps", icon: "MousePointer", items: ["Hotjar", "FullStory", "Microsoft Clarity"] },
    { title: "Testing", icon: "TestTube", items: ["VWO", "Optimizely", "Google Optimize"] },
    { title: "Surveys", icon: "MessageSquare", items: ["Typeform", "SurveyMonkey", "Qualaroo"] },
  ],
  techTitle: "CRO Tools",
  techDescription: "Industry-leading tools for analysis, testing, and optimization.",
  qualification: {
    forYou: [
      "You have consistent traffic but conversion rates are underperforming",
      "You're ready to make decisions based on data, not opinions",
      "You understand that optimization is an ongoing process",
      "You have budget to implement winning tests",
    ],
    notForYou: [
      "You're looking for a one-time quick fix",
      "You don't have enough traffic to run meaningful tests",
      "You're not willing to implement test winners",
      "You expect guarantees on specific conversion rate improvements",
    ],
  },
  faqs: [
    { question: "How long does it take to see CRO results?", answer: "Initial insights come within weeks. Statistically significant test results typically take 2-4 weeks depending on traffic. Compounding improvements are seen over 3-6 months of consistent testing." },
    { question: "What's a realistic conversion rate improvement?", answer: "Most clients see 20-50% improvement in conversion rates within 6 months. But the real value is building a testing culture that compounds improvements over time." },
    { question: "Do we need a lot of traffic for CRO?", answer: "Higher traffic means faster test results. With lower traffic, we focus on high-impact changes and qualitative research. We'll recommend the right approach for your traffic level." },
    { question: "How do you prioritize what to test?", answer: "We use a framework that balances potential impact, confidence in hypothesis, and ease of implementation. We test high-impact, high-confidence hypotheses first." },
  ],
  finalCtaTitle: "Optimize what you already have.",
  finalCtaDescription: "Before spending more on acquisition, maximize the value of your existing traffic.",
  finalCtaButtonText: "Request a CRO audit",
};

const CRO = () => {
  const { content } = usePageContent("cro", fallback);

  const services = content.services.map((s: any) => ({ ...s, icon: resolveIcon(s.icon) }));
  const technologyCategories = content.technologyCategories.map((t: any) => ({ ...t, icon: resolveIcon(t.icon) }));

  return (
    <SubServicePageLayout
      seoTitle={content.seoTitle}
      seoDescription={content.seoDescription}
      canonicalUrl={content.canonicalUrl}
      category={content.category}
      categoryLabel={content.categoryLabel}
      headline={content.headline}
      subheadline={content.subheadline}
      ctaText={content.ctaText}
      problemTitle={content.problemTitle}
      problems={content.problems}
      systemTitle={content.systemTitle}
      systemDescription={content.systemDescription}
      services={services}
      systemShift={content.systemShift}
      engagementShape={content.engagementShape}
      processTitle={content.processTitle}
      processSteps={content.processSteps}
      technologyCategories={technologyCategories}
      techTitle={content.techTitle}
      techDescription={content.techDescription}
      qualification={content.qualification}
      faqs={content.faqs}
      finalCtaTitle={content.finalCtaTitle}
      finalCtaDescription={content.finalCtaDescription}
      finalCtaButtonText={content.finalCtaButtonText}
    />
  );
};

export default CRO;
